// src/store/apis/estimateApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ------------------------------ helpers ------------------------------ */
const key = (x) => String(x ?? "");
const sameId = (a, b) => key(a) === key(b);
const sameDoc = (it, id) => sameId(it?._id ?? it?.id, id);

const pickData = (res) => res?.data ?? res?.entry ?? res;

const toNum = (v) => Number(v ?? 0) || 0;
const round2 = (x) => Math.round((toNum(x) + Number.EPSILON) * 100) / 100;
const round3 = (x) => Math.round((toNum(x) + Number.EPSILON) * 1000) / 1000;

const normLimit = (args, draft) =>
  Number(args?.limit ?? draft?.pagination?.limit ?? draft?.limit ?? 10) || 10;

/* -------------------------- ids normalizer -------------------------- */
// supports: "id", 123, { id }, { ids: [] }, { idsCsv: "a,b,c" }
const normalizeIdsArg = (arg) => {
  const out = [];

  if (arg == null) return out;

  if (typeof arg === "string" || typeof arg === "number") {
    out.push(String(arg));
    return [...new Set(out.filter(Boolean))];
  }

  if (typeof arg === "object") {
    if (arg.id != null) out.push(String(arg.id));
    if (Array.isArray(arg.ids)) out.push(...arg.ids.map((x) => String(x)));
    if (arg.idsCsv) {
      out.push(
        ...String(arg.idsCsv)
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
      );
    }
  }

  return [...new Set(out.filter(Boolean))];
};

/* ----------------------------- totals utils ----------------------------- */
const incTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* --------------------------- list manipulation --------------------------- */
const addIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.rows)) draft.rows = [];
  if (!newItems?.length) return;

  // if current list is not page 1, don't insert rows (avoid unexpected list changes),
  // only increase totals.
  if (args.page && Number(args.page) !== 1) {
    incTotals(draft, newItems.length);
    return;
  }

  draft.rows.unshift(...newItems);

  const lim = normLimit(args, draft);
  if (lim > 0 && draft.rows.length > lim) {
    draft.rows.splice(lim);
  }
  incTotals(draft, newItems.length);
};

/* --------------------------- filter matching --------------------------- */
const matchesEstFilters = (est = {}, args = {}) => {
  const {
    branchId = "",
    customerId = "",
    from = "",
    to = "",
    search = "", // client-side only
    includeHidden = "0",
  } = args || {};

  const rowBranchId = est?.branchId?._id ?? est?.branchId;
  const rowCustomerId = est?.customerId?._id ?? est?.customerId;

  if (branchId && !sameId(rowBranchId, branchId)) return false;
  if (customerId && !sameId(rowCustomerId, customerId)) return false;

  // hide filter: backend default hidden exclude unless includeHidden=1
  const rowHidden = !!(est?.isHidden ?? false);
  if (String(includeHidden) !== "1" && rowHidden) return false;

  if (from || to) {
    const d = new Date(est?.estDate || est?.estimateDate || est?.createdAt);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
  }

  if (search) {
    const hay = [
      String(est?.estNo ?? ""),
      est?.estCode,
      est?.notes,
      est?.customerId?.customerName,
      est?.customerId?.customerId,
      est?.branchId?.branchName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(String(search).toLowerCase())) return false;
  }

  return true;
};

/* ----------------------- compute totals (client) ----------------------- */
const computeEstimateTotals = (
  stockEntries = [],
  looseStones = [],
  looseDiamonds = [],
  opts = {},
) => {
  const discount = toNum(opts?.totalDiscount);
  const courier = toNum(opts?.courierCharge);
  const gstRoundOff = !!opts?.gstRoundOff;

  const t = {
    items: 0,
    qty: 0,
    totalWt: 0,
    packingWt: 0,
    grossWt: 0,
    stoneWt: 0,
    diamondWt: 0,
    netWt: 0,
    stonePcs: 0,
    diamondPcs: 0,

    subTotal: 0,
    gstTotal: 0,
    lineGrand: 0,

    discount: round2(discount),
    courier: round2(courier),
    roundOff: 0,

    grandTotal: 0,
  };

  for (const it of stockEntries || []) {
    t.items += 1;
    t.qty += toNum(it?.qty);
    t.totalWt += toNum(it?.totalWt);
    t.packingWt += toNum(it?.packingWt);
    t.grossWt += toNum(it?.grossWt);
    t.stoneWt += toNum(it?.stoneWt);
    t.diamondWt += toNum(it?.diamondWt);
    t.netWt += toNum(it?.netWt);
    t.stonePcs += toNum(it?.stonePcs);
    t.diamondPcs += toNum(it?.diamondPcs);
    t.subTotal += toNum(it?.itemAmount);
    t.gstTotal += toNum(it?.itemGstAmount);
    t.lineGrand += toNum(it?.totalItemAmount);
  }
  for (const s of looseStones || []) {
    t.items += 1;
    t.subTotal += toNum(s?.itemAmount);
    t.gstTotal += toNum(s?.itemGstAmount);
    t.lineGrand += toNum(s?.totalItemAmount);
  }
  for (const d of looseDiamonds || []) {
    t.items += 1;
    t.subTotal += toNum(d?.itemAmount);
    t.gstTotal += toNum(d?.itemGstAmount);
    t.lineGrand += toNum(d?.totalItemAmount);
  }

  const gross = t.lineGrand - t.discount + t.courier;
  const rounded = Math.round(gross);
  t.roundOff = gstRoundOff ? round2(rounded - gross) : 0;
  t.grandTotal = round2(gross + t.roundOff);

  t.totalWt = round3(t.totalWt);
  t.packingWt = round3(t.packingWt);
  t.grossWt = round3(t.grossWt);
  t.stoneWt = round3(t.stoneWt);
  t.diamondWt = round3(t.diamondWt);
  t.netWt = round3(t.netWt);

  return t;
};

/* ------------------- default args + cache seed/upsert ------------------- */
const defaultListArgs = (fromArgs = {}) => ({
  page: Number(fromArgs.page) || 1,
  limit: Number(fromArgs.limit) || 10,
  branchId: fromArgs.branchId || "",
  customerId: fromArgs.customerId || "",
  from: fromArgs.from || "",
  to: fromArgs.to || "",
  search: fromArgs.search || "",
  sortBy: fromArgs.sortBy,
  sortOrder: fromArgs.sortOrder,
  includeHidden: fromArgs.includeHidden ?? "0",
  q: fromArgs.q || "", // backend search by estCode (optional)
});

const ensureListCacheAndInject = (dispatch, getState, args, rows) => {
  const a = defaultListArgs(args);
  const sel = estimateApi.endpoints.getEstimates.select(a)(getState());
  if (sel?.data) {
    dispatch(
      estimateApi.util.updateQueryData("getEstimates", a, (draft) => {
        addIntoListDraft(draft, a, rows);
      }),
    );
  } else {
    const lim = normLimit(a);
    // NOTE: seed draft keeps same shape the UI expects
    const draft = {
      rows: [],
      total: 0,
      page: a.page,
      limit: lim,
      summary: null,
      pageSummary: null,
    };
    addIntoListDraft(draft, a, rows);
    dispatch(estimateApi.util.upsertQueryData("getEstimates", a, draft));
  }
};

const upsertOrPatchRow = (dispatch, getState, args, updatedRow) => {
  const a = defaultListArgs(args);
  const sel = estimateApi.endpoints.getEstimates.select(a)(getState());
  if (sel?.data) {
    dispatch(
      estimateApi.util.updateQueryData("getEstimates", a, (draft) => {
        if (!Array.isArray(draft?.rows)) draft.rows = [];
        const idx = draft.rows.findIndex((it) => sameDoc(it, updatedRow?._id));
        if (idx >= 0) {
          draft.rows[idx] = { ...draft.rows[idx], ...updatedRow };
        } else if (matchesEstFilters(updatedRow, a)) {
          addIntoListDraft(draft, a, [updatedRow]);
        }
      }),
    );
  } else if (matchesEstFilters(updatedRow, a)) {
    const lim = normLimit(a);
    dispatch(
      estimateApi.util.upsertQueryData("getEstimates", a, {
        rows: [updatedRow].slice(0, lim),
        total: 1,
        page: a.page,
        limit: lim,
        summary: null,
        pageSummary: null,
      }),
    );
  }
};

/* -------------------- build optimistic row from form -------------------- */
const buildTempEstimateRow = (payload, tempId) => {
  const nowIso = new Date().toISOString();

  const stockEntries = Array.isArray(payload?.stockEntries)
    ? payload.stockEntries.map((r) => ({
        qty: toNum(r?.qty),
        totalWt: toNum(r?.totalWt),
        packingWt: toNum(r?.packingWt),
        grossWt: toNum(r?.grossWt),
        stoneWt: toNum(r?.stoneWt),
        diamondWt: toNum(r?.diamondWt),
        netWt: toNum(r?.netWt),
        stonePcs: toNum(r?.stonePcs),
        diamondPcs: toNum(r?.diamondPcs),
        itemAmount: toNum(r?.itemAmount),
        itemGstAmount: toNum(r?.itemGstAmount),
        totalItemAmount: toNum(r?.totalItemAmount),

        itemCode: r?.itemCode?.label || r?.itemCode || "",
        categoryName: r?.category?.label || r?.categoryName || "",
        productName: r?.product?.label || r?.productName || "",
        designName: r?.design?.label || r?.designName || "",
        purityName: r?.purity?.label || r?.purityName || "",
      }))
    : [];

  const looseStones = Array.isArray(payload?.looseStones)
    ? payload.looseStones.map((s) => ({
        itemAmount: toNum(s?.itemAmount),
        itemGstAmount: toNum(s?.itemGstAmount),
        totalItemAmount: toNum(s?.totalItemAmount),
        stoneName:
          s?.stnPopupName?.label ||
          s?.stnPopupName?.info?.stoneName ||
          s?.stoneName ||
          "",
        unit: s?.unit || s?.stnPopupName?.info?.unit || "",
        wt: toNum(s?.stnPopupWt ?? s?.wt),
        pcs: toNum(s?.stnPopupPcs ?? s?.pcs),
      }))
    : [];

  const looseDiamonds = Array.isArray(payload?.looseDiamonds)
    ? payload.looseDiamonds.map((d) => ({
        itemAmount: toNum(d?.itemAmount),
        itemGstAmount: toNum(d?.itemGstAmount),
        totalItemAmount: toNum(d?.totalItemAmount),
        diamondName:
          d?.diaName?.label ||
          d?.diaName?.info?.diamondName ||
          d?.diamondName ||
          "",
        unit: "carat",
        ct: toNum(d?.diaWt ?? d?.ct),
        pcs: toNum(d?.pcs),
      }))
    : [];

  const totals = computeEstimateTotals(
    stockEntries,
    looseStones,
    looseDiamonds,
    {
      totalDiscount: payload?.totalDiscount,
      courierCharge: payload?.courierCharge,
      gstRoundOff: payload?.gstRoundOff,
    },
  );

  const branch = payload?.branch;
  const customer = payload?.customer;

  return {
    _id: tempId,
    estNo: toNum(payload?.estNo) || undefined, // normally backend assigns
    estCode: payload?.estCode || undefined, // normally backend assigns
    estDate: payload?.estimateDate
      ? new Date(payload.estimateDate).toISOString()
      : nowIso,
    notes: payload?.notes || "",

    branchId: branch
      ? { _id: branch.value, branchName: branch.label }
      : payload?.branchId
        ? { _id: payload.branchId }
        : undefined,

    customerId: customer
      ? {
          _id: customer.value,
          customerName: customer?.info?.customerName || customer?.label || "",
          customerId: customer?.info?.customerId || customer?.label || "",
          contact: customer?.info?.contact,
        }
      : payload?.customerId
        ? { _id: payload.customerId }
        : undefined,

    stockEntries,
    looseStones,
    looseDiamonds,
    totals,

    gstApply: !!payload?.gstApply,
    gstRoundOff: !!payload?.gstRoundOff,
    tdsApply: !!payload?.tdsApply,

    isActive: true,
    isHidden: false,
    __temp: true,
  };
};

/* ---------------------- cache helpers for bulk ops ---------------------- */
const removeIdsFromListDraft = (draft, idsSet) => {
  if (!Array.isArray(draft?.rows) || !idsSet?.size) return 0;
  const before = draft.rows.length;
  draft.rows = draft.rows.filter(
    (it) => !idsSet.has(String(it?._id ?? it?.id)),
  );
  return before - draft.rows.length;
};

const setHiddenOnRowsDraft = (draft, idsSet, isHidden, listArgs) => {
  if (!Array.isArray(draft?.rows) || !idsSet?.size)
    return { changed: 0, removed: 0 };

  const includeHidden = String(listArgs?.includeHidden ?? "0") === "1";
  let changed = 0;
  let removed = 0;

  // if we are hiding and list doesn't includeHidden => remove rows
  if (isHidden && !includeHidden) {
    const before = draft.rows.length;
    draft.rows = draft.rows.filter((it) => {
      const id = String(it?._id ?? it?.id);
      return !idsSet.has(id);
    });
    removed = before - draft.rows.length;
    return { changed: 0, removed };
  }

  // else just patch flag
  for (let i = 0; i < draft.rows.length; i++) {
    const id = String(draft.rows[i]?._id ?? draft.rows[i]?.id);
    if (idsSet.has(id)) {
      draft.rows[i] = { ...draft.rows[i], isHidden: !!isHidden };
      changed++;
    }
  }

  return { changed, removed: 0 };
};

/* ------------------------ normalize list response ------------------------ */
/**
 * IMPORTANT:
 * Some backends return:
 *   { rows, total, summary, pageSummary }
 * and some return:
 *   { data: { rows, total, summary, pageSummary } }
 * SalesList me aap direct data.rows use kar rahe ho.
 * So yaha list response ko SAME shape me normalize kar dete hai.
 */
const normalizeListRes = (res) => {
  const d = pickData(res) || {};
  return {
    ...d,
    rows: Array.isArray(d?.rows) ? d.rows : [],
    summary: d?.summary ?? null,
    pageSummary: d?.pageSummary ?? null,
  };
};

/* --------------------------------------------------------------------- */

const sanitizeEstimatePatchForCache = (patch = {}) => {
  const p = { ...patch };

  // react-select option objects (these crash list render if UI prints them)
  if (p.branch && typeof p.branch === "object") delete p.branch;
  if (p.customer && typeof p.customer === "object") delete p.customer;

  // (optional) agar stockEntries me bhi category/product/design/purity select objects aate ho
  if (Array.isArray(p.stockEntries)) {
    p.stockEntries = p.stockEntries.map((r) => {
      const rr = { ...r };
      if (rr.itemCode && typeof rr.itemCode === "object")
        rr.itemCode = rr.itemCode.label;
      if (rr.category && typeof rr.category === "object") {
        rr.categoryId = rr.category.value;
        rr.categoryName = rr.category.label;
        delete rr.category;
      }
      if (rr.product && typeof rr.product === "object") {
        rr.productId = rr.product.value;
        rr.productName = rr.product.label;
        delete rr.product;
      }
      if (rr.design && typeof rr.design === "object") {
        rr.designId = rr.design.value;
        rr.designName = rr.design.label;
        delete rr.design;
      }
      if (rr.purity && typeof rr.purity === "object") {
        rr.purityId = rr.purity.value;
        rr.purityName = rr.purity.label;
        delete rr.purity;
      }
      return rr;
    });
  }

  return p;
};

export const estimateApi = createApi({
  reducerPath: "estimateApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["EST", "EST_LAST_NO"],
  endpoints: (builder) => ({
    /* ------------------------------ LIST ------------------------------ */
    getEstimates: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 20,
          branchId = "",
          customerId = "",
          from = "",
          to = "",
          q = "", // backend: estCode search
          sortBy,
          sortOrder,
          includeHidden = "0",
        } = args;

        const params = { page, limit, includeHidden };
        if (branchId) params.branchId = branchId;
        if (customerId) params.customerId = customerId;
        if (from) params.from = from;
        if (to) params.to = to;
        if (q) params.q = q;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return { url: "/estimates", method: "GET", params };
      },

      // ✅ FIX: summary/pageSummary drop nahi honge, wrapper {data:{...}} bhi handle
      transformResponse: (res) => normalizeListRes(res),

      providesTags: ["EST"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.branchId,
          a.customerId,
          a.from,
          a.to,
          a.search, // client-only key
          a.q, // backend search
          a.sortBy,
          a.sortOrder,
          a.includeHidden ?? "0",
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ----------------------------- GET ONE --------------------------- */
    getEstimateById: builder.query({
      query: (id) => `/estimates/${id}`,
      providesTags: (_res, _err, id) => [{ type: "EST", id: key(id) }],
    }),

    /* ----------------------------- CREATE ---------------------------- */
    addEstimate: builder.mutation({
      query: (payload) => ({
        url: "/estimates",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const tempId = `tmp_est_${Date.now()}`;
        const tempRow = buildTempEstimateRow(payload, tempId);

        const undoPatches = [];
        const allArgs = estimateApi.util.selectCachedArgsForQuery(
          getState(),
          "getEstimates",
        );

        for (const args of allArgs) {
          if (!matchesEstFilters(tempRow, args)) continue;
          undoPatches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                args,
                (draft) => {
                  addIntoListDraft(draft, args, [tempRow]);
                },
              ),
            ),
          );
        }

        ensureListCacheAndInject(dispatch, getState, { page: 1, limit: 20 }, [
          tempRow,
        ]);

        try {
          const { data: res } = await queryFulfilled;
          const created = pickData(res);
          const real = Array.isArray(created) ? created[0] : created;
          if (!real) return;

          const again = estimateApi.util.selectCachedArgsForQuery(
            getState(),
            "getEstimates",
          );

          for (const args of again) {
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const pos = draft.rows.findIndex((r) => sameDoc(r, tempId));
                  const nowMatches = matchesEstFilters(real, args);

                  if (pos >= 0) {
                    if (nowMatches) {
                      draft.rows[pos] = { ...draft.rows[pos], ...real };
                      delete draft.rows[pos].__temp;
                    } else {
                      draft.rows.splice(pos, 1);
                      decTotals(draft, 1);
                    }
                  } else if (nowMatches) {
                    addIntoListDraft(draft, args, [real]);
                  }
                },
              ),
            );
          }
        } catch {
          undoPatches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["EST_LAST_NO"],
    }),

    /* ----------------------------- UPDATE ---------------------------- */
    updateEstimate: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/estimates/${id}`,
        method: "PUT",
        body: patch,
      }),

      async onQueryStarted(
        { id, ...patch },
        { dispatch, queryFulfilled, getState },
      ) {
        const safePatch = sanitizeEstimatePatchForCache(patch);

        const patches = [];
        const allArgs = estimateApi.util.selectCachedArgsForQuery(
          getState(),
          "getEstimates",
        );

        // ✅ optimistic (safe only)
        for (const args of allArgs) {
          patches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                  if (idx >= 0) {
                    draft.rows[idx] = { ...draft.rows[idx], ...safePatch };
                  }
                },
              ),
            ),
          );
        }

        patches.push(
          dispatch(
            estimateApi.util.updateQueryData("getEstimateById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, safePatch);
            }),
          ),
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = pickData(res);
          if (!updated) return;

          // ✅ IMPORTANT: replace row to drop leftover keys like branch/customer
          for (const args of allArgs) {
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;

                  const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                  const nowMatches = matchesEstFilters(updated, args);
                  if (idx >= 0) {
                    if (nowMatches) {
                      draft.rows[idx] = updated; // ✅ replace (not merge)
                    } else {
                      draft.rows.splice(idx, 1);
                      decTotals(draft, 1);
                    }
                  } else if (nowMatches) {
                    addIntoListDraft(draft, args, [updated]);
                  }
                },
              ),
            );
          }

          dispatch(
            estimateApi.util.updateQueryData("getEstimateById", id, (draft) => {
              if (draft?.data) draft.data = updated; // ✅ replace
            }),
          );
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ----------------------------- DELETE (single + bulk) ---------------------------- */
    // usage:
    // 1) deleteEstimate(id)
    // 2) deleteEstimate({ id })
    // 3) deleteEstimate({ ids: [...] })
    // 4) deleteEstimate({ ids: [...], atomic: true })  // if backend supports ?atomic=1
    deleteEstimate: builder.mutation({
      query: (arg) => {
        const ids = normalizeIdsArg(arg);
        const atomic = !!(arg && typeof arg === "object" && arg.atomic);

        // single id -> keep old style url if you want
        if (
          ids.length === 1 &&
          (typeof arg === "string" || typeof arg === "number" || arg?.id)
        ) {
          const params = {};
          if (atomic) params.atomic = 1;
          return { url: `/estimates/${ids[0]}`, method: "DELETE", params };
        }

        // bulk -> DELETE /estimates?ids=a,b,c
        const params = {};
        if (ids.length) params.ids = ids.join(",");
        if (atomic) params.atomic = 1;

        return { url: "/estimates", method: "DELETE", params };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const ids = normalizeIdsArg(arg);
        const idsSet = new Set(ids.map(String));

        const patches = [];
        const allArgs = estimateApi.util.selectCachedArgsForQuery(
          getState(),
          "getEstimates",
        );

        // optimistic remove from all list caches
        for (const args of allArgs) {
          patches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                args,
                (draft) => {
                  const removed = removeIdsFromListDraft(draft, idsSet);
                  if (removed > 0) decTotals(draft, removed);
                },
              ),
            ),
          );
        }

        // optimistic mark in detail caches (if open)
        for (const id of ids) {
          patches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimateById",
                id,
                (draft) => {
                  if (draft?.data) {
                    draft.data.isActive = false;
                    draft.data.__deleted = true;
                  }
                },
              ),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["EST_LAST_NO"],
    }),

    /* ----------------------------- HIDE/UNHIDE (single + bulk) ---------------------------- */
    // usage:
    // 1) toggleHideEstimate({ id, isHidden: true/false })
    // 2) toggleHideEstimate({ ids: [...], isHidden: true/false })
    toggleHideEstimate: builder.mutation({
      query: (arg = {}) => {
        const ids = normalizeIdsArg(arg);
        const isHidden = !!arg?.isHidden;

        // unified route: PATCH /estimates/hide
        const body =
          ids.length <= 1 ? { id: ids[0], isHidden } : { ids, isHidden };

        return {
          url: "/estimates/hide",
          method: "PATCH",
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const ids = normalizeIdsArg(arg);
        const idsSet = new Set(ids.map(String));
        const isHidden = !!arg?.isHidden;

        const patches = [];
        const allArgs = estimateApi.util.selectCachedArgsForQuery(
          getState(),
          "getEstimates",
        );

        // optimistic: update lists
        for (const listArgs of allArgs) {
          patches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimates",
                listArgs,
                (draft) => {
                  const { removed } = setHiddenOnRowsDraft(
                    draft,
                    idsSet,
                    isHidden,
                    listArgs,
                  );
                  if (removed > 0) decTotals(draft, removed);
                },
              ),
            ),
          );
        }

        // optimistic: update detail cache(s)
        for (const id of ids) {
          patches.push(
            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimateById",
                id,
                (draft) => {
                  if (draft?.data) draft.data.isHidden = isHidden;
                },
              ),
            ),
          );
        }

        try {
          const { data: res } = await queryFulfilled;

          // backend might return counts only; if it returns a doc, patch it
          const maybe = pickData(res)?.data ?? pickData(res);
          const updatedDoc =
            maybe && typeof maybe === "object" && (maybe._id || maybe.id)
              ? maybe
              : null;

          if (updatedDoc) {
            const uid = String(updatedDoc._id || updatedDoc.id);

            for (const listArgs of allArgs) {
              dispatch(
                estimateApi.util.updateQueryData(
                  "getEstimates",
                  listArgs,
                  (draft) => {
                    const includeHidden =
                      String(listArgs?.includeHidden ?? "0") === "1";
                    const idx = Array.isArray(draft?.rows)
                      ? draft.rows.findIndex((it) => sameDoc(it, uid))
                      : -1;

                    if (isHidden && !includeHidden) {
                      if (idx >= 0) {
                        draft.rows.splice(idx, 1);
                        decTotals(draft, 1);
                      }
                      return;
                    }

                    if (idx >= 0) {
                      draft.rows[idx] = { ...draft.rows[idx], ...updatedDoc };
                    } else if (matchesEstFilters(updatedDoc, listArgs)) {
                      addIntoListDraft(draft, listArgs, [updatedDoc]);
                    }
                  },
                ),
              );
            }

            dispatch(
              estimateApi.util.updateQueryData(
                "getEstimateById",
                uid,
                (draft) => {
                  if (draft?.data) Object.assign(draft.data, updatedDoc);
                },
              ),
            );
          }
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ------------------------- LAST EST NUMBER ------------------------ */
    getLastEstimateNumber: builder.query({
      query: (args = {}) => {
        const { branchId = "", date = "" } = args || {};
        const params = {};
        if (branchId) params.branchId = branchId;
        if (date) params.date = date;
        return { url: "/estimates/last-number", method: "GET", params };
      },
      providesTags: ["EST_LAST_NO"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [endpointName, a.branchId || "", a.date || ""].join("|");
      },
      keepUnusedDataFor: 30,
      refetchOnMountOrArgChange: true,
    }),

    downloadEstimateReceiptPdf: builder.mutation({
      query: (id) => ({
        url: `/estimates/${id}/receipt.pdf`,
        method: "GET",
        responseHandler: (response) => response.text(),
      }),
    }),
  }),
});

/* ----------------------------- hooks ----------------------------- */
export const {
  useGetEstimatesQuery,
  useGetEstimateByIdQuery,
  useAddEstimateMutation,
  useUpdateEstimateMutation,
  useDeleteEstimateMutation,
  useToggleHideEstimateMutation,
  useGetLastEstimateNumberQuery,
  useDownloadEstimateReceiptPdfMutation,
} = estimateApi;

export default estimateApi;
